import type { BoundsDefinition, PolygonDefinition, RingDefinition } from "./types";

function getFirstRing(geometry: GeoJSON.Geometry): GeoJSON.Position[] | null {
	if (geometry.type === "Polygon") {
		return geometry.coordinates[0] ?? null;
	}

	if (geometry.type === "MultiPolygon") {
		return geometry.coordinates[0]?.[0] ?? null;
	}

	return null;
}

function getRingBounds(ring: RingDefinition): BoundsDefinition {
	let west = Number.POSITIVE_INFINITY;
	let south = Number.POSITIVE_INFINITY;
	let east = Number.NEGATIVE_INFINITY;
	let north = Number.NEGATIVE_INFINITY;

	for (const point of ring) {
		west = Math.min(west, point.lng);
		south = Math.min(south, point.lat);
		east = Math.max(east, point.lng);
		north = Math.max(north, point.lat);
	}

	return { west, south, east, north };
}

function getRingCenter(ring: RingDefinition): { lng: number; lat: number } {
	const { west, south, east, north } = getRingBounds(ring);

	return { lng: (west + east) / 2, lat: (south + north) / 2 };
}

/**
 * Extent of every part of a multi-part geometry. Holes are inside their outline by definition, so only
 * outlines are scanned. `null` when there is no geometry to measure.
 */
function getPolygonsBounds(polygons: Array<PolygonDefinition>): BoundsDefinition | null {
	const points = polygons.flatMap((polygon) => polygon.outline);

	return points.length === 0 ? null : getRingBounds(points);
}

function getGeometryCenter(geometry: GeoJSON.Geometry): { lng: number; lat: number } | null {
	const ring = getFirstRing(geometry);

	if (ring === null) {
		return null;
	}

	return getRingCenter(ring.map(([lng, lat]) => ({ lng, lat })));
}

export { getGeometryCenter, getPolygonsBounds, getRingBounds, getRingCenter };
